<script lang="ts">
import { cloneDeep } from 'lodash'
import { computed, defineComponent, PropType, ref } from 'vue'

import { opChain } from '@collector/shared-utils'
import {
  CricketResultId,
  IllegalBallAttributesId,
  IllegalBallAttributesOptions,
  Result,
} from '@collector/sportsapi-client-legacy'
import { ProbableIncident } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'
import UpdateIncident from '@mobile/ActionQueue/Actions/Incidents/UpdateIncident'
import Button from '@mobile/components/Button/Button.vue'
import ToggleButtonGroup, {
  ToggleButtonGroupOption,
} from '@mobile/components/ToggleButton/ToggleButtonGroup.vue'
import { byId } from '@mobile/reusables/entityUtils'
import { incidentDiff } from '@mobile/reusables/eventUtils'
import { useAttributesToggleOptions } from '@mobile/views/Relation/Shared/Popup/builder/buttons/useAttributesToggleOptions'
import PopupControls from '@mobile/views/Relation/Shared/Popup/PopupControls.vue'
import PopupTitle from '@mobile/views/Relation/Shared/Popup/PopupTitle.vue'
import { ProbableLinkedParticipant } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import IncrementableNumber from '@mobile/views/Relation/Sports/Cricket/LiveView/IncrementableNumber/IncrementableNumber.vue'
import IncrementableNumberValueChangeContent from '@mobile/views/Relation/Sports/Cricket/LiveView/IncrementableNumber/IncrementableNumberValueChangeContent.vue'
import IncrementableNumberWithWickets from '@mobile/views/Relation/Sports/Cricket/LiveView/IncrementableNumber/IncrementableNumberWithWickets.vue'

export default defineComponent({
  components: {
    PopupControls,
    Button,
    IncrementableNumber,
    IncrementableNumberWithWickets,
    IncrementableNumberValueChangeContent,
    ToggleButtonGroup,
    PopupTitle,
  },
  props: {
    incident: {
      type: Object as PropType<ProbableIncident>,
      required: true,
    },
    probableLinkedParticipant: {
      type: Object as PropType<ProbableLinkedParticipant>,
      required: true,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    // composable
    const eventInjections = useEventInjections()

    // data
    const { actionQueue, probableEvent } = eventInjections
    const { id: eventId } = probableEvent.value
    const participant = props.probableLinkedParticipant.getParticipant()
    const attributeInputOptions: ToggleButtonGroupOption[] =
      useAttributesToggleOptions(
        [
          IllegalBallAttributesId.Wides,
          IllegalBallAttributesId.NoBallBat,
          IllegalBallAttributesId.NoBall,
        ],
        eventInjections,
        false,
      )

    // state
    const staticLinkedIncident = ref(cloneDeep(props.incident))
    const currentOver = ref(props.incident.additional_info?.current_over || 0)
    const currentBall = ref(props.incident.additional_info?.current_ball || 0)
    const currentAction = ref(
      props.incident.additional_info?.current_action || 0,
    )

    // computed
    const wickets = computed(
      () => byId(participant.value.results, CricketResultId.Wickets) as Result,
    )
    const scoreBeforeChange = computed(() => {
      const ballAttributesOption = [...IllegalBallAttributesOptions].find(
        (option) => {
          return staticLinkedIncident.value.attribute_ids.find(
            (attribute) => attribute === option.id,
          )
        },
      )

      return ballAttributesOption?.value as number
    })
    const runsOffBallValue = computed(() => {
      const illegalBallAttributesOption = IllegalBallAttributesOptions.find(
        (option) => {
          return props.incident.attribute_ids.find(
            (attribute) => attribute === option.id,
          )
        },
      )
      const defaultOption = {
        id: IllegalBallAttributesId.Zero,
        value: 0,
      }

      return illegalBallAttributesOption ?? defaultOption
    })

    const selectedAttributeValue = computed(() => {
      return attributeInputOptions.find((option) => {
        return props.incident.attribute_ids.find(
          (attribute) => attribute === option.value,
        )
      })
    })
    const runsOffBall = ref(runsOffBallValue.value)
    const selectedAttribute = ref(selectedAttributeValue.value?.value || null)

    // methods
    function updateIncident(): void {
      const diff = incidentDiff(props.incident, staticLinkedIncident.value)

      actionQueue.value.add(
        new UpdateIncident(props.incident.id, {
          incident_id: props.incident.incident_id,
          event_id: eventId,
          status_id: props.incident.event_status_id,
          attribute_ids: [
            runsOffBall.value?.id ?? null,
            Number(selectedAttribute.value),
          ],
          participant_team_id: props.incident.participant_id,
          participant_id: diff.subparticipant_id,
          participant_name: diff.subparticipant_name,
          assistant_id: diff.assistant_id,
          assistant_name: diff.assistant_name,
          additional_info: {
            current_over: currentOver.value,
            current_ball: currentBall.value,
          },
          minute: null,
          second: null,
        }),
      )

      emit('close')
    }

    return {
      runsOffBall,
      selectedAttribute,
      attributeInputOptions,
      updateIncident,
      IllegalBallAttributesOptions,
      scoreBeforeChange,
      wickets,
      currentOver,
      currentBall,
      currentAction,
      opChain,
    }
  },
})
</script>

<template>
  <div class="bg-silver">
    <PopupTitle>
      <template #title>Edit Illegal ball</template>
    </PopupTitle>
  </div>
  <div class="mr-4 ml-4 grid rounded-md">
    <div class="flex justify-end">
      <IncrementableNumberWithWickets
        v-model:value="runsOffBall"
        class="col-span-2"
        :scoreBeforeChange
        :wickets
        :options="IllegalBallAttributesOptions"
      />
    </div>
    <div class="mt-2 w-full">
      <ToggleButtonGroup
        v-model:value="selectedAttribute"
        :options="attributeInputOptions"
        type="grid"
        :undeselectable="true"
      />
    </div>
    <div class="flex justify-between pt-3">
      <IncrementableNumber
        v-model="currentOver"
        title="Current over"
        :min="0"
        :max="100"
      >
        <template #content>
          <IncrementableNumberValueChangeContent>
            <template #leftSide>
              {{
                opChain(
                  incident.additional_info,
                  ({ current_over }) => current_over || '-',
                )
              }}
            </template>
            <template #rightSide>{{ currentOver }}</template>
          </IncrementableNumberValueChangeContent>
        </template>
      </IncrementableNumber>
      <IncrementableNumber
        v-model="currentBall"
        title="Current ball"
        :min="0"
        :max="100"
      >
        <template #content>
          <IncrementableNumberValueChangeContent>
            <template #leftSide>
              {{
                opChain(
                  incident.additional_info,
                  ({ current_ball }) => current_ball || '-',
                )
              }}
            </template>
            <template #rightSide>{{ currentBall }}</template>
          </IncrementableNumberValueChangeContent>
        </template>
      </IncrementableNumber>
    </div>
  </div>
  <PopupControls>
    <template #buttons>
      <div class="flex justify-end space-x-2">
        <Button
          :color="'red'"
          :size="'lg'"
          @click="$emit('close')"
        >
          Close
        </Button>
        <Button
          :color="'green'"
          :size="'lg'"
          :disabled="!selectedAttribute"
          @click="updateIncident()"
        >
          Send
        </Button>
      </div>
    </template>
  </PopupControls>
</template>
